import { Box, Dialog, DialogContent, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Activity, useCallback, useEffect, useId, useState } from "react";

/** Distance (in px) from the top of the scroll container within which the "more content" chevron is still shown. */
const SCROLL_TOP_THRESHOLD = 4;

/** Structure of the props that dialog box accepts. */
interface MicrosoftInsightsDialogProps {
  /** Width settings to determine the dialog's maximum width; use false to disable max-width constraint. Defaults to "md". */
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
  /** Boolean value to control whether the dialog is open or closed. When true, the dialog is displayed; when false, it is hidden. */
  open: boolean;
  /** Action performed to close the dialog. */
  onClose: () => void;
  /** Title of the dialog. */
  title: string;
  /** Content of the dialog. */
  children: React.ReactNode;
}

/**
 * Creates a dialog component that serves as a reusable template for Microsoft Insights-related dialogs. 
 * @param props Configuration options to manage the dialog's open state and handle closing actions.
 * @returns A dialog component with instructional content.
 */
export function MicrosoftInsightsDialog(props: MicrosoftInsightsDialogProps): React.ReactNode {
  /** Unique ID for the dialog title, used for accessibility. */
  const titleId = useId();
  /** The scrollable dialog content node; tracked in state (not a plain ref) since MUI mounts it into its portal on a later render pass. */
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  /** Whether the floating "more content below" chevron should be shown. */
  const [showScrollChevron, setShowScrollChevron] = useState(false);

  const contentRef = useCallback((node: HTMLDivElement | null) => {
    setContentNode(node);
  }, []);

  /** Recomputes chevron visibility based on the container's current scroll position and overflow. */
  function updateScrollChevronVisibility(target: HTMLDivElement): void {
    setShowScrollChevron(
      target.scrollTop <= SCROLL_TOP_THRESHOLD && target.scrollHeight > target.clientHeight,
    );
  }

  useEffect(() => {
    if (!props.open || !contentNode) return;

    updateScrollChevronVisibility(contentNode);

    const resizeObserver = new ResizeObserver(() => updateScrollChevronVisibility(contentNode));
    resizeObserver.observe(contentNode);

    return () => resizeObserver.disconnect();
  }, [props.open, contentNode, props.children]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={props.maxWidth ?? "md"}
      fullWidth
      aria-labelledby={titleId}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography id={titleId} variant="h6">
          {props.title}
        </Typography>
        <IconButton size="small" onClick={props.onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      {/* flex/minHeight preserve MuiDialogContent's native scroll behavior now that it's nested under a positioning wrapper. */}
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
        <DialogContent
          ref={contentRef}
          onScroll={(event) => updateScrollChevronVisibility(event.currentTarget)}
          sx={{
            bgcolor: "background.default",
            px: 3,
            py: 3,
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {props.children}
        </DialogContent>
        <Activity mode={showScrollChevron ? "visible" : "hidden"}>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "background.paper",
              boxShadow: 2,
              pointerEvents: "none",
            }}
          >
            <KeyboardArrowDownIcon fontSize="medium" color="action" />
          </Box>
        </Activity>
      </Box>
    </Dialog>
  );
}
